import React, { useCallback, useMemo, useState } from 'react';
import {
  InternalApiAttributeType,
  InternalApiCase,
  InternalApiLogicalOperator,
  InternalApiSearchFilter,
  InternalApiSearchOperator,
  InternalApiSortDirection,
} from '../api-internal';
import {
  getDebouncedFn,
  SalvButton,
  SalvDropdown,
  SalvProgressCircle,
  SalvSearchField,
  SalvTable,
  SalvTablePaginationType,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { CAN_READ_CASE } from '../auth/permissions';
import SalvPage from '../common/SalvPage';
import { getCasePath, InternalNavigation } from '../navigation/helper';
import { ColumnSort, createColumnHelper, PaginationState, SortingState } from '@tanstack/react-table';
import { Updater } from '@tanstack/table-core/src/types';
import { useMe } from '../organisation/useMe';
import { useQueryCaseRationaleOptions, useQueryCaseStatusOptions } from './caseQueryActions';
import { useQueryCasesPage } from './casesQueryActions';
import { formatDate } from '../common/datetime/FormattedDateTime';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import { getOrderByAndDirection } from '../common/sort';
import { SearchParamKeys } from '../common/searchParamKeys';
import { useAssignableUsers } from '../users/useAssignableUsers';
import { convertToDropdownOption } from '../common/helper';
import './Cases.scss';

const DEFAULT_ORDER_BY = 'createdAt';
const DEFAULT_DIRECTION = InternalApiSortDirection.DESC;

const Cases = () => {
  const { permissions } = useMe();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = searchParams.get(SearchParamKeys.page) ? Number(searchParams.get(SearchParamKeys.page)) : 0;
  const query = searchParams.get(SearchParamKeys.query) || '';
  const statusCode = searchParams.get(SearchParamKeys.statusCode) || '';
  const assigneeUserId = searchParams.get(SearchParamKeys.assigneeUserId) || '';
  const rationaleCode = searchParams.get(SearchParamKeys.rationaleCode) || '';
  const orderBy = searchParams.get(SearchParamKeys.orderBy) || DEFAULT_ORDER_BY;
  const direction = (searchParams.get(SearchParamKeys.direction) as InternalApiSortDirection) || DEFAULT_DIRECTION;

  const {
    data: rationaleOptions,
    isPending: isRationaleOptionsPending,
    isError: isRationaleOptionsError,
    error: rationaleOptionsError,
  } = useQueryCaseRationaleOptions();

  const {
    data: statusOptions,
    isPending: isStatusOptionsPending,
    isError: isStatusOptionsError,
    error: statusOptionsError,
  } = useQueryCaseStatusOptions();

  const { assignableUsers, isLoading: isAssignableUsersLoading } = useAssignableUsers();

  const assigneeOptions = useMemo(
    () => assignableUsers.map((user) => convertToDropdownOption(user.name, user.id)),
    [assignableUsers]
  );

  const columnSort: ColumnSort = { id: orderBy, desc: direction === InternalApiSortDirection.DESC };

  const filters = useMemo(() => {
    const result: InternalApiSearchFilter[] = [];
    if (query) {
      result.push({
        attributeIdentifier: 'query',
        attributeType: InternalApiAttributeType.GENERAL,
        operator: InternalApiSearchOperator.CONTAINS,
        not: false,
        query,
      });
    }
    if (statusCode) {
      result.push({
        attributeIdentifier: 'statusCode',
        attributeType: InternalApiAttributeType.GENERAL,
        operator: InternalApiSearchOperator.IS_EXACTLY,
        not: false,
        query: statusCode,
      });
    }
    if (assigneeUserId) {
      result.push({
        attributeIdentifier: 'assigneeUserId',
        attributeType: InternalApiAttributeType.GENERAL,
        operator: InternalApiSearchOperator.IS_EXACTLY,
        not: false,
        query: assigneeUserId,
      });
    }
    if (rationaleCode) {
      result.push({
        attributeIdentifier: 'rationaleCode',
        attributeType: InternalApiAttributeType.GENERAL,
        operator: InternalApiSearchOperator.IS_EXACTLY,
        not: false,
        query: rationaleCode,
      });
    }
    return result;
  }, [query, statusCode, assigneeUserId, rationaleCode]);

  const setPagination = useCallback(
    (newPagination: PaginationState) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);

          if (newPagination.pageIndex === 0) {
            newParams.delete(SearchParamKeys.page);
          } else {
            newParams.set(SearchParamKeys.page, String(newPagination.pageIndex));
          }

          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [queryInput, setQueryInput] = useState(query);

  const { debouncedFn: debouncedSetQuery } = useMemo(
    () =>
      getDebouncedFn((value: string) => {
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);

            if (value) {
              newParams.set(SearchParamKeys.query, value);
            } else {
              newParams.delete(SearchParamKeys.query);
            }

            newParams.delete(SearchParamKeys.page);
            return newParams;
          },
          { replace: true }
        );
      }, 300),
    [setSearchParams]
  );

  const setQuery = (value: string) => {
    setQueryInput(value);
    debouncedSetQuery(value);
  };

  const setFilterParam = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
        newParams.delete(SearchParamKeys.page);
        return newParams;
      },
      { replace: true }
    );
  };

  const setSorting = useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      const { orderBy: newOrderBy = DEFAULT_ORDER_BY, direction: newDirection = DEFAULT_DIRECTION } =
        getOrderByAndDirection(updaterOrValue, [columnSort]);

      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (newOrderBy === DEFAULT_ORDER_BY) {
            newParams.delete(SearchParamKeys.orderBy);
          } else {
            newParams.set(SearchParamKeys.orderBy, newOrderBy);
          }

          if (newDirection === DEFAULT_DIRECTION) {
            newParams.delete(SearchParamKeys.direction);
          } else {
            newParams.set(SearchParamKeys.direction, newDirection);
          }

          newParams.delete(SearchParamKeys.page);
          return newParams;
        },
        { replace: true }
      );
    },
    [columnSort, setSearchParams]
  );

  const columnHelper = createColumnHelper<InternalApiCase>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('details.name', {
        id: 'name',
        header: 'Name',
        enableSorting: true,
      }),
      columnHelper.accessor('details.description', {
        id: 'description',
        header: 'Description',
        enableSorting: true,
      }),
      columnHelper.accessor('details.rationaleCode', {
        header: 'Rationale',
        cell: ({ getValue }) => {
          const code = getValue();
          if (isRationaleOptionsPending || isRationaleOptionsError) {
            return [];
          }
          return rationaleOptions.find((option) => option.value === code)!.label;
        },
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created at',
        cell: ({ getValue, row }) => (
          <SalvTooltipWrapper
            tooltip={`${row.original.createdBy.name}, ${formatDate(getValue(), true)}`}
            placement='top'
          >
            {formatDate(getValue())}
          </SalvTooltipWrapper>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor((row) => row.assignment?.assignee.name, {
        id: 'assignee',
        header: 'Assignee',
      }),
      columnHelper.accessor('assignedStatus.code', {
        id: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const code = getValue();
          if (isStatusOptionsPending || isStatusOptionsError) {
            return [];
          }
          return statusOptions.find((option) => option.value === code)?.label ?? '';
        },
      }),
    ],
    [
      rationaleOptions,
      isRationaleOptionsPending,
      isRationaleOptionsError,
      statusOptions,
      isStatusOptionsPending,
      isStatusOptionsError,
    ]
  );

  const {
    data: casesPage,
    isPending: isCasesPending,
    isError,
    error,
  } = useQueryCasesPage({
    filters,
    logicalOperator: InternalApiLogicalOperator.AND,
    page,
    orderBy: columnSort.id,
    direction: columnSort.desc ? InternalApiSortDirection.DESC : InternalApiSortDirection.ASC,
  });

  if (isError || isRationaleOptionsError || isStatusOptionsError) {
    return (
      <DataLoadingErrorFallback
        message='Could not load Cases'
        error={[error, rationaleOptionsError, statusOptionsError]}
      />
    );
  }

  return (
    <SalvPage
      title={'Cases'}
      actions={
        <SalvButton
          label='New Case'
          icon={faPlus}
          size={Size.SMALL}
          onClick={() => navigate(InternalNavigation.caseCreation)}
        />
      }
    >
      <div className='m-b-16 salv-case-filters' style={{ display: 'flex', gap: '8px' }}>
        <SalvSearchField
          label={'Name/Description'}
          value={queryInput}
          onChange={(value) => setQuery(value)}
          placeholder='Search'
          size={Size.SMALL}
        />
        <SalvDropdown
          label='Status'
          placeholder='All'
          options={statusOptions ?? []}
          value={statusOptions?.find((o) => o.value === statusCode)}
          onChange={([option]) => setFilterParam(SearchParamKeys.statusCode, option?.value)}
          size={Size.SMALL}
        />
        <SalvDropdown
          label='Assignee'
          placeholder='All'
          options={assigneeOptions}
          value={assigneeOptions.find((o) => o.value === assigneeUserId)}
          onChange={([option]) => setFilterParam(SearchParamKeys.assigneeUserId, option?.value)}
          size={Size.SMALL}
        />
        <SalvDropdown
          label='Rationale'
          placeholder='All'
          options={rationaleOptions ?? []}
          value={rationaleOptions?.find((o: DropdownOption) => o.value === rationaleCode)}
          onChange={([option]) => setFilterParam(SearchParamKeys.rationaleCode, option?.value)}
          size={Size.SMALL}
        />
      </div>
      {isCasesPending || isRationaleOptionsPending || isStatusOptionsPending || isAssignableUsersLoading ? (
        <SalvProgressCircle ariaLabel={'Loading Cases'} />
      ) : (
        <SalvTable
          data={casesPage.content}
          columns={columns}
          emptyMessage={filters.length > 0 ? 'No cases match your filters.' : 'No cases yet'}
          hover={permissions.includes(CAN_READ_CASE)}
          rowClick={
            permissions.includes(CAN_READ_CASE) ? (row) => navigate(getCasePath(row.original.handle)) : undefined
          }
          pagination={{
            type: SalvTablePaginationType.PAGINATION,
            total: casesPage.total,
            state: { pageIndex: casesPage.number, pageSize: casesPage.size },
            setPagination,
          }}
          sorting={{
            state: [columnSort],
            setSorting,
          }}
        />
      )}
    </SalvPage>
  );
};

export default Cases;
